import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  QrCode,
  Download,
  Share2,
  Copy,
  Check,
  Smartphone,
  CreditCard,
} from 'lucide-react';

const QRPayment = ({ campaignId, campaignTitle, suggestedAmount = 50 }) => {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  // Generate the quick payment URL
  const paymentUrl = `${window.location.origin}/donate/${campaignId}?quick=true&amount=${suggestedAmount}`;
  
  // Handle copy link
  const handleCopy = () => {
    navigator.clipboard.writeText(paymentUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Handle download QR code
  const handleDownload = () => {
    const svg = document.getElementById(`qr-${campaignId}`);
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code
      ctx.drawImage(img, 50, 50, 300, 300);
      
      // Add campaign title
      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Scan to Donate', canvas.width / 2, 30);
      ctx.font = '12px Arial';
      ctx.fillText(campaignTitle?.substring(0, 40) + (campaignTitle?.length > 40 ? '...' : ''), canvas.width / 2, 380);
      
      const link = document.createElement('a');
      link.download = `donate-qr-${campaignId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };
  
  // Handle share
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Donate to: ${campaignTitle}`,
          text: 'Scan the QR code or click the link to donate quickly!',
          url: paymentUrl,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full border-emerald-500/50 hover:bg-emerald-500/10 text-foreground"
        >
          <QrCode className="w-4 h-4 mr-2 text-emerald-400" />
          QR Code Payment
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-center text-foreground flex items-center justify-center gap-2">
            <QrCode className="w-5 h-5 text-emerald-400" />
            Quick QR Payment
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-6 py-4">
          {/* QR Code */}
          <div className="bg-white p-4 rounded-xl shadow-lg">
            <QRCodeSVG
              id={`qr-${campaignId}`}
              value={paymentUrl}
              size={200}
              level="H"
              includeMargin={true}
              fgColor="#0f172a"
              bgColor="#ffffff"
              imageSettings={{
                src: '/logo-icon.png',
                height: 30,
                width: 30,
                excavate: true,
              }}
            />
          </div>
          
          {/* Instructions */}
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Share this QR code with donors for quick payment
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Smartphone className="w-4 h-4" /> Scan with phone
              </span>
              <span className="flex items-center gap-1">
                <CreditCard className="w-4 h-4" /> Pay instantly
              </span>
            </div>
          </div>
          
          {/* Payment Methods */}
          <div className="bg-secondary rounded-lg p-3 w-full">
            <p className="text-xs text-muted-foreground text-center mb-2">Accepts all payment methods</p>
            <div className="flex justify-center gap-2">
              <div className="bg-card px-3 py-1.5 rounded text-xs font-medium">Credit Card</div>
              <div className="bg-card px-3 py-1.5 rounded text-xs font-medium">Apple Pay</div>
              <div className="bg-card px-3 py-1.5 rounded text-xs font-medium">Google Pay</div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="flex-1 border-border"
            >
              <Download className="w-4 h-4 mr-1" /> Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="flex-1 border-border"
            >
              {copied ? (
                <><Check className="w-4 h-4 mr-1 text-green-500" /> Copied!</>
              ) : (
                <><Copy className="w-4 h-4 mr-1" /> Copy Link</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="flex-1 border-border"
            >
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
          </div>
          
          {/* Direct Link */}
          <div className="w-full">
            <p className="text-xs text-muted-foreground mb-1">Or share this link:</p>
            <div className="bg-secondary rounded-lg p-2 text-xs text-foreground break-all font-mono">
              {paymentUrl}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRPayment;
